/**
 * TimelineTaskTrackerLayer Component
 *
 * SVG layer that renders a mouse-tracking vertical line with a "+" bubble
 * for multi-submission tasks (single_completion === false). Clicking the "+"
 * opens the task submission popper.
 *
 * The tracker line lives inside the SVG. The "+" bubble is portaled into
 * contentRef as an absolutely-positioned div at z-index 16, so it renders
 * above the now line (z-index 15).
 */

import React, { useState, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Button, useTheme } from '@mui/material'
import { useTimelineStaticContext } from '../../context/timeline-static-context'
import { useTimelineStyles } from '../../../hooks/use-timeline-styles'
import {
  EXPANDED_ROW_HEIGHT,
  EXPANDED_ROW_HEIGHT_GAP,
  VERTICAL_DAY_TRACKER_SPACE,
} from '../../timeline-constants'
import { TimelineTaskSubmissionPopper } from './timeline-task-submission-popper'
import { TimelinePopsicleBadge, POPSICLE_BADGE_R } from '../content/timeline-popsicle-badge'
import { ControlPointDuplicate } from '@mui/icons-material'
import { useFeatures } from '~/common/use-features'

const BUBBLE_R = 10
const BUBBLE_TOP_GAP = 5

/**
 * TimelineTaskTrackerLayer - Mouse tracker for multi-submission tasks
 *
 * Renders per task where single_completion === false:
 * - Invisible capture rect (SVG) to track mouse X within task bounds
 * - Dashed vertical tracker line (SVG) at mouse X
 * - "+" bubble (portaled HTML div, z-index 16) at top of rect, follows mouse X
 *
 * @param {Object} props
 * @param {Array} props.crops - Transformed crop data with week-based tasks
 */
export const TimelineTaskTrackerLayer = React.memo(({ crops, hideBadges = false }) => {
  const {
    expandedIds,
    weekWidth,
    getRowY,
    forceAllExpand,
    viewStrategy,
    contentRef,
    readOnly,
    collapsedRowHeight,
    collapsedRowHeightGap,
  } = useTimelineStaticContext()

  const { getTaskStyle } = useTimelineStyles()
  const { features } = useFeatures()
  const theme = useTheme()

  const [hoveredTracker, setHoveredTracker] = useState(null) // { taskId, x }
  const [popperState, setPopperState] = useState(null) // { open, task, anchorX, anchorY }
  const [portalTarget, setPortalTarget] = useState(null)

  useEffect(() => {
    setPortalTarget(contentRef.current)
  }, [contentRef])

  const handleMouseMove = useCallback((e, taskId, rectX, rectWidth) => {
    const containerRect = contentRef.current.getBoundingClientRect()
    const rawX = e.clientX - containerRect.left + contentRef.current.scrollLeft
    const clampedX = Math.max(rectX, Math.min(rectX + rectWidth, rawX))
    setHoveredTracker({ taskId, x: clampedX })
  }, [contentRef])

  const handleMouseLeave = useCallback((taskId) => {
    setHoveredTracker(prev => (prev?.taskId === taskId ? null : prev))
  }, [])

  const handleBubbleClick = useCallback((e, task) => {
    if (readOnly) return
    e.stopPropagation()
    setPopperState({ open: true, task, anchorX: e.clientX, anchorY: e.clientY })
  }, [readOnly])

  const handlePopperClose = useCallback(() => {
    setPopperState(null)
  }, [])

  return (
    <>
      {crops.map((crop) => {
        const isExpanded = forceAllExpand || expandedIds.includes(crop.id)
        const rowY = getRowY(crop.id) + VERTICAL_DAY_TRACKER_SPACE * 8

        const baseRowH = isExpanded ? EXPANDED_ROW_HEIGHT : collapsedRowHeight
        const rowH = viewStrategy === 'both' && !crop.isOrgTaskRow ? baseRowH * 2 : baseRowH
        const barH = isExpanded
          ? EXPANDED_ROW_HEIGHT - EXPANDED_ROW_HEIGHT_GAP
          : collapsedRowHeight - collapsedRowHeightGap
        const subRowH = viewStrategy === 'both' && !crop.isOrgTaskRow ? rowH / 2 : rowH
        const y = viewStrategy === 'both' && !crop.isOrgTaskRow
          ? rowY + subRowH + (subRowH / 2) - (barH / 2)
          : rowY + (rowH / 2) - (barH / 2)

        const multiSubmitTasks = crop.tasks?.filter(t => t.single_completion === false) ?? []

        return (
          <g key={`${crop.id}-task-tracker`}>
            {multiSubmitTasks.map((task) => {
              const rectX = task.start * weekWidth
              const rectWidth = task.len * weekWidth
              const isHovered = hoveredTracker?.taskId === task.id
              const trackerX = isHovered ? hoveredTracker.x : null
              const taskStyle = getTaskStyle(task.type)
              const taskColor = taskStyle.color
              const taskIcon = taskStyle.icon

              // Compute badge cx values so we can suppress the bubble when near one
              const badgeCxs = task.submissions?.map(sub =>
                Math.max(rectX + POPSICLE_BADGE_R + 2, Math.min(rectX + rectWidth - POPSICLE_BADGE_R - 2, sub.submitted_at_weeks * weekWidth))
              ) ?? []
              const isNearBadge = trackerX !== null && badgeCxs.some(cx => Math.abs(cx - trackerX) <= POPSICLE_BADGE_R + 4)

              return (
                // onMouseLeave on the wrapper fires only when truly exiting the entire task area
                <g
                  key={`task-tracker-${task.id}`}
                  onMouseLeave={() => handleMouseLeave(task.id)}
                >
                  {/* Invisible capture rect — tracks mouse X within task bounds */}
                  <rect
                    x={rectX}
                    y={y}
                    width={rectWidth}
                    height={barH}
                    fill="transparent"
                    onMouseMove={(e) => handleMouseMove(e, task.id, rectX, rectWidth)}
                  />

                  {/* Tracker line — vertical, clamped to task rect */}
                  {isHovered && trackerX !== null && (
                    <g style={{ pointerEvents: 'none' }}>
                      <line
                        x1={trackerX}
                        y1={y}
                        x2={trackerX}
                        y2={y + barH}
                        stroke={taskColor}
                        strokeWidth={1.5}
                        strokeDasharray="3,2"
                        opacity={0.9}
                      />
                    </g>
                  )}

                  {/* Submission badges — rendered after the capture rect so they sit on top in SVG z-order */}
                  {!hideBadges && task.submissions?.map((sub) => {
                    const cx = Math.max(
                      rectX + POPSICLE_BADGE_R + 2,
                      Math.min(rectX + rectWidth - POPSICLE_BADGE_R - 2, sub.submitted_at_weeks * weekWidth)
                    )
                    return (
                      <TimelinePopsicleBadge
                        key={sub.id}
                        cx={cx}
                        y={y}
                        color={theme.palette.success.main}
                        icon={taskIcon}
                        onClick={(e) => {
                          e.stopPropagation()
                          setPopperState({ open: true, task, anchorX: e.clientX, anchorY: e.clientY, submission: sub })
                        }}
                      />
                    )
                  })}

                  {/* "+" bubble — portaled above TimelineHoverLine (z-index 21); hidden when near an existing badge */}
                  {features['log_harvest'] && isHovered && trackerX !== null && !isNearBadge && portalTarget && createPortal(
                    <Button
                      variant='text'
                      onClick={(e) => handleBubbleClick(e, task)}
                      onMouseMove={(e) => handleMouseMove(e, task.id, rectX, rectWidth)}
                      onMouseLeave={() => handleMouseLeave(task.id)}
                      sx={{
                        position: 'absolute',
                        left: trackerX - BUBBLE_R + 1,
                        top: y + BUBBLE_TOP_GAP,
                        minWidth: 0,
                        p: 0,
                        height: collapsedRowHeight / 2,
                        zIndex: 21,
                        backgroundColor: taskColor,
                      }}
                    >
                      <ControlPointDuplicate sx={{ fontSize: BUBBLE_R * 2, color: 'white' }} />
                    </Button>,
                    portalTarget
                  )}
                </g>
              )
            })}
          </g>
        )
      })}

      {/* Submission popper — portaled by MUI outside SVG */}
      {features['log_harvest'] && popperState?.open && (
        <TimelineTaskSubmissionPopper
          open={popperState.open}
          task={popperState.task}
          anchorX={popperState.anchorX}
          anchorY={popperState.anchorY}
          onClose={handlePopperClose}
          submission={popperState.submission ?? null}
        />
      )}
    </>
  )
})
